import type { GeoPoint, LocationStore } from '@metro/location'
import { appLocation } from './homeRegion'
import type { GeoStation, NearestStationProvider } from './nearestStationProvider'

/**
 * TASK-391 §2b — metro location-consumer service.
 *
 * The `@metro/location` package is pure, domain-agnostic location: it
 * only emits a raw `userCoordinate`. This service is **metro app code**
 * (the precursor to Suggester V2). It subscribes to `Location`'s
 * `userCoordinate` and owns everything domain-specific:
 *
 * - the city's home-region test + airport remap (`appPoint`, see §3.5;
 *   London: the UK box and Heathrow);
 * - the nearest-station resolution over the station schema/database;
 * - the derived state the §3 behavior (launch snap, button branches, the
 *   user-location dot) reads.
 *
 * It deliberately mirrors the `LocationStore` shape (`getState` /
 * `subscribe`) so hosts treat it as the single domain-aware handle.
 */

/** Observable derived state owned by the metro location-consumer service. */
export interface LocationConsumerState {
  /** Latest raw user coordinate, or `null` before the first fix. */
  readonly userPoint: GeoPoint | null
  /** Coordinate after the home-region / airport remap, or `null` before the first fix. */
  readonly appPoint: GeoPoint | null
  /** Nearest stations to `appPoint`, ordered by distance. */
  readonly nearestStations: readonly GeoStation[]
}

const initialState: LocationConsumerState = {
  userPoint: null,
  appPoint: null,
  nearestStations: [],
}

export interface LocationConsumerService {
  /** Latest derived domain state. */
  getState: () => LocationConsumerState
  /** Subscribe to derived-state changes; returns a disposer. */
  subscribe: (listener: () => void) => () => void
  /**
   * Takes one location fix and resolves the derived domain state from it,
   * returning the freshly-resolved state. Use this on the discrete
   * launch / button-tap paths (§3.2 / §3.3) where the caller needs the
   * nearest station deterministically right after the fix — the
   * subscription path alone is fire-and-forget.
   */
  requestNearest: () => Promise<LocationConsumerState>
  /** Stops syncing from the underlying `Location` store. */
  dispose: () => void
}

export interface LocationConsumerDeps {
  /** The pure `Location` store this service consumes. */
  readonly store: LocationStore
  /** Resolves the N nearest stations to a coordinate. */
  readonly nearestProvider: NearestStationProvider
  /** Nearest-station limit (route feature: 1). @defaultValue 1 */
  readonly nearestLimit?: number
}

/**
 * Builds the metro location-consumer service. It subscribes to the
 * `Location` store, and whenever the raw `userCoordinate` changes it
 * applies the home-region / airport remap and resolves the nearest station(s),
 * publishing the derived `LocationConsumerState` to its own subscribers.
 */
export function createLocationConsumerService(deps: LocationConsumerDeps): LocationConsumerService {
  const limit = deps.nearestLimit ?? 1
  let state = initialState
  let lastUserPoint: GeoPoint | null = null
  const listeners = new Set<() => void>()

  const notify = (): void => {
    for (const l of listeners) l()
  }

  const recompute = async (): Promise<void> => {
    const userPoint = deps.store.getState().userCoordinate
    // The store fires for permission changes too, and starts with a
    // `null` coordinate; only re-resolve when the raw coordinate is
    // present and has actually moved. The `Location` store never
    // clears `userCoordinate` back to `null`, so this is the only guard.
    if (userPoint === null || userPoint === lastUserPoint) return
    lastUserPoint = userPoint

    const appPoint = appLocation(userPoint)
    const nearestStations = await deps.nearestProvider.nearestStations(appPoint, limit)
    state = { userPoint, appPoint, nearestStations }
    notify()
  }

  const unsubscribeStore = deps.store.subscribe(() => {
    void recompute()
  })
  // Pick up any coordinate already present at construction time.
  void recompute()

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    requestNearest: async () => {
      await deps.store.dispatch({ kind: 'RequestFix' })
      // `RequestFix` updates `userCoordinate` synchronously inside the
      // store dispatch; `recompute` then re-resolves the domain state.
      await recompute()
      return state
    },
    dispose: () => {
      unsubscribeStore()
      listeners.clear()
    },
  }
}
